import { FC, useState, useContext } from 'react';
import { ActionsDropdown, Button, Icon, Select, Modal, TextArea } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../../../Localization';
import { ConfigurationContext } from '../../../Context';
import { HeaderProps } from './shared/types';

export const Header: FC<HeaderProps> = ({
  caseInfo,
  onBack,
  status,
  onStatusChange,
  assignedTo,
  onAssignedToChange,
  assigneeOptions,
  assignees,
  statusOptions,
  updatingStatus,
  updatingAssignee,
  onReportSpam,
}) => {
  const L = useLocalizedStrings();
  const config = useContext(ConfigurationContext);
  const [showSpamModal, setShowSpamModal] = useState(false);
  const [spamReason, setSpamReason] = useState('');
  const [isReportingSpam, setIsReportingSpam] = useState(false);

  // Check if user is admin
  const isAdmin = (config.roles || '').includes('admin');

  // Handle spam report submission
  const handleReportSpam = async () => {
    if (!onReportSpam) return;

    setIsReportingSpam(true);
    try {
      await onReportSpam(spamReason);
      setShowSpamModal(false);
      setSpamReason('');
    } catch (error) {
      console.error('Error reporting spam:', error);
    } finally {
      setIsReportingSpam(false);
    }
  };

  // Custom renderer for status button to show colored dot
  const renderStatusButton = (value: string) => {
    const statusOption = statusOptions.find(opt => opt.value === value);
    if (!statusOption) return value;

    // Get the status name from the option value
    const statusName = value.charAt(0).toUpperCase() + value.slice(1);

    // Determine dot color based on status
    let dotColor = 'bg-gray-500';
    if (value === 'open') dotColor = 'bg-yellow-500';
    else if (value === 'resolved') dotColor = 'bg-teal-500';
    else if (value === 'escalated') dotColor = 'bg-red-500';

    return (
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${dotColor}`} />
        <span>{statusName}</span>
      </div>
    );
  };

  // Custom renderer for assignee button to show user icon
  const renderAssigneeButton = (value: string) => {
    // Look up the assignee name from the assignees array
    const assignee = assignees.find(a => a.uid === value);

    // If unassigned, use localized string, otherwise use assignee name or value
    let assigneeName;
    if (value === 'unassigned') {
      assigneeName = L.support.unassigned;
    } else if (assignee) {
      assigneeName = assignee.name;
    } else {
      assigneeName = value;
    }

    // Don't show icon if unassigned
    const isUnassigned = value === 'unassigned' || !assignee;

    return (
      <div className="flex items-center gap-2">
        {!isUnassigned && <Icon role="decorative" type="solid" name="UserIcon" className="shrink-0 w-4 h-4 text-gray-500" />}
        <span className="whitespace-nowrap overflow-hidden">{assigneeName}</span>
      </div>
    );
  };

  return (
    <div className="bg-white border-b px-4 py-3 flex items-center justify-between" style={{ width: '100%' }}>
      <div className="flex items-center gap-4">
        <Button
          variant="secondary"
          onClick={onBack}
          icon={<Icon role="decorative" type="solid" name="ChevronLeftIcon" />}
        >
          {L.support.back}
        </Button>
        <div className="flex items-center gap-2">
          <div className="font-semibold">
            {L.support.case} {caseInfo.uid.split('-')[0]}, {caseInfo.contact}
          </div>
          {isAdmin && (
            <ActionsDropdown
              actions={[
                {
                  label: L.support.report_spam,
                  onClick: () => setShowSpamModal(true),
                  icon: "ExclamationTriangleIcon"
                }
              ]}
              item={caseInfo.contact}
              className="ml-auto -my-1 p-0 leading-none flex items-center"
            />
          )}
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">{L.stats.current_status}:</span>
          <div className="w-48">
            <Select
              hideLabel
              label={L.stats.current_status}
              description={L.support.update_status}
              value={status}
              onChange={onStatusChange}
              options={statusOptions}
              placeholder={L.support.update_status}
              customButtonTextRenderer={renderStatusButton}
              disabled={updatingStatus || caseInfo.status === 'resolved' || caseInfo.status === 'closed'}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">{L.support.assigned_to}</span>
          <div className="w-48">
            <Select
              hideLabel
              label={L.support.assigned_to}
              description={L.support.assigned_to}
              value={assignedTo}
              onChange={onAssignedToChange}
              options={assigneeOptions}
              placeholder={L.support.unassigned}
              customButtonTextRenderer={renderAssigneeButton}
              disabled={updatingAssignee}
            />
          </div>
        </div>
      </div>

      {/* Spam Report Modal */}
      <Modal
        open={showSpamModal}
        onClose={() => {
          setShowSpamModal(false);
          setSpamReason('');
        }}
        title={L.support.report_spam}
        size="md"
      >
        <div className="space-y-3">
          <div className="mt-2">
            <div className="text-sm text-gray-600">
              {L.support.you_are_about_to_report_this_contact}
            </div>
            <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
              <li>{L.support.add_to_blocklist}</li>
              <li>{L.support.resolve_and_close}</li>
            </ul>
          </div>

          <TextArea
            label={L.support.reason_optional}
            showLabel={true}
            value={spamReason}
            onChange={(e) => setSpamReason(e.target.value)}
            rows={3}
            placeholder={L.support.enter_reason_spam}
          />

          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              onClick={() => {
                setShowSpamModal(false);
                setSpamReason('');
              }}
              disabled={isReportingSpam}
            >
              {L.support.cancel}
            </Button>
            <Button
              variant="primary"
              onClick={handleReportSpam}
              disabled={isReportingSpam}
            >
              {L.support.report_spam}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
